import { FEATURE_FLAGS, REDIS_FEATURE_FLAGS } from '../../../../featureFlags';
import { frontendURL } from '../../../../helper/URLHelper';
import {
  lockedFeatureRouteName,
  lockedFeatureURL,
} from '../../../../constants/lockedFeatures';

// Claudia-only accounts keep the entry visible and route it to the upsell page.
// The account feature flag is dropped on purpose: the sidebar filter would hide
// the item for accounts that have the real feature disabled, which is exactly
// the audience the upsell is for.
const lockedVariant = (item, accountId, feature) => ({
  ...item,
  featureFlag: undefined,
  claudiaOnlyLocked: true,
  toState: frontendURL(lockedFeatureURL(accountId, feature)),
  toStateName: lockedFeatureRouteName(feature),
});

const primaryMenuItems = (
  accountId,
  locale = 'pt_BR',
  claudiaOnly = false,
  role = null
) => {
  // Map locale to URL code
  const localeMap = {
    en: 'en',
    es: 'es',
  };

  const faqLocale = localeMap[locale] || 'pt_BR';
  const faqUrl = `https://cloudchat.cloudhumans.com/hc/cloud-chat/${faqLocale}`;

  const claudiaOnlySettingsEntry =
    role === 'administrator'
      ? {
          toState: frontendURL(`accounts/${accountId}/settings/agents`),
          toStateName: 'agent_list',
        }
      : {
          toState: frontendURL(`accounts/${accountId}/settings/labels/list`),
          toStateName: 'labels_list',
        };

  const items = [
    {
      icon: 'mail-inbox',
      key: 'inboxView',
      label: 'INBOX_VIEW',
      featureFlag: FEATURE_FLAGS.INBOX_VIEW,
      toState: frontendURL(`accounts/${accountId}/inbox-view`),
      toStateName: 'inbox_view',
    },
    {
      icon: 'chat-alt-6',
      key: 'conversations',
      label: 'CONVERSATIONS',
      keepInClaudiaOnly: true,
      toState: frontendURL(`accounts/${accountId}/dashboard`),
      toStateName: 'home',
    },
    {
      icon: 'captain',
      key: 'captain',
      label: 'CAPTAIN',
      featureFlag: FEATURE_FLAGS.CAPTAIN,
      toState: frontendURL(`accounts/${accountId}/captain`),
      toStateName: 'captain',
    },
    {
      icon: 'claudia',
      key: 'claudia',
      label: 'CLAUDIA',
      redisFeatureFlag: REDIS_FEATURE_FLAGS.CLAUDIA,
      keepInClaudiaOnly: true,
      toState: frontendURL(`accounts/${accountId}/claudia`),
      toStateName: 'claudia_app',
    },
    {
      icon: 'users-2',
      key: 'contacts',
      label: 'CONTACTS',
      featureFlag: FEATURE_FLAGS.CRM,
      toState: frontendURL(`accounts/${accountId}/contacts`),
      toStateName: 'contacts_dashboard',
    },
    {
      icon: 'call',
      key: 'voice',
      label: 'VOICE',
      toState: frontendURL(`accounts/${accountId}/voice`),
      toStateName: 'voice_teaser',
    },
    {
      icon: 'graph-increase',
      key: 'reports',
      label: 'REPORTS',
      featureFlag: FEATURE_FLAGS.REPORTS,
      // In Claudia-only mode the Reports section is collapsed to the Omni report
      // builder only, so the primary nav item points straight at create-mode.
      toState: claudiaOnly
        ? frontendURL(`accounts/${accountId}/reports/create-mode`)
        : frontendURL(`accounts/${accountId}/reports`),
      toStateName: claudiaOnly
        ? 'reports_create_mode'
        : 'account_overview_reports',
      keepInClaudiaOnly: claudiaOnly ? true : undefined,
    },
    {
      icon: 'megaphone-2',
      key: 'campaigns',
      label: 'CAMPAIGNS',
      featureFlag: FEATURE_FLAGS.CAMPAIGNS,
      toState: frontendURL(`accounts/${accountId}/campaigns`),
      toStateName: 'one_off',
    },
    {
      icon: 'library',
      key: 'helpcenter',
      label: 'HELP_CENTER.TITLE',
      featureFlag: FEATURE_FLAGS.HELP_CENTER,
      alwaysVisibleOnChatwootInstances: true,
      toState: frontendURL(`accounts/${accountId}/portals`),
      toStateName: 'default_portal_articles',
    },
    {
      icon: 'cog',
      key: 'settings',
      label: 'SETTINGS',
      ...(claudiaOnly
        ? claudiaOnlySettingsEntry
        : {
            toState: frontendURL(`accounts/${accountId}/settings`),
            toStateName: 'settings_home',
          }),
      keepInClaudiaOnly: claudiaOnly ? true : undefined,
    },
    {
      icon: 'question-circle',
      key: 'faq',
      label: 'FAQ',
      openInNewPage: true,
      keepInClaudiaOnly: true,
      toState: faqUrl,
      toStateName: 'faq',
    },
    {
      icon: 'invention',
      key: 'product-feedback',
      label: 'PRODUCT_FEEDBACK',
      openInNewPage: true,
      keepInClaudiaOnly: true,
      toState: 'https://cloudhumans.canny.io/feature-request-cloud-chat',
      toStateName: 'product_feedback',
    },
  ];

  if (!claudiaOnly) return items;

  const lockedByKey = { campaigns: 'campaigns', helpcenter: 'help_center' };
  return items.map(item =>
    lockedByKey[item.key]
      ? lockedVariant(item, accountId, lockedByKey[item.key])
      : item
  );
};

export default primaryMenuItems;
